import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EmailOtpStep from "./EmailOtpStep";
import { AuthService } from "@/services/auth/authService";
import { toast } from "sonner";
import { authLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import type { LoginResponse } from "@/services/auth/authService";
import { getEmailOtpErrorMessage } from "@/lib/auth/emailOtpErrors";

interface EmailOtpLoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  password: string;
  onVerified: (loginResponse: LoginResponse) => void;
}

const formatMmSs = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const EmailOtpLoginDialog = ({
  open,
  onOpenChange,
  email,
  password,
  onVerified,
}: EmailOtpLoginDialogProps) => {
  const { language: lang } = useLanguage();
  const labels = authLabels(lang);
  const otpLabels = labels.login?.otp ?? labels.bankIdRegister?.otp ?? {};

  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpExpiresAtMs, setOtpExpiresAtMs] = useState<number | null>(null);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [autoSent, setAutoSent] = useState(false);

  useEffect(() => {
    if (!open) {
      setOtpCode("");
      setOtpExpiresAtMs(null);
      setResendCooldown(0);
      setAutoSent(false);
      return;
    }
    if (!autoSent && email) {
      setAutoSent(true);
      void sendOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- send once per open
  }, [open, email, autoSent]);

  useEffect(() => {
    if (!otpExpiresAtMs) {
      setOtpSecondsLeft(0);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((otpExpiresAtMs - Date.now()) / 1000));
      setOtpSecondsLeft(left);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [otpExpiresAtMs]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(
      () => setResendCooldown((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => window.clearTimeout(id);
  }, [resendCooldown]);

  const sendOtp = async () => {
    setOtpSending(true);
    try {
      const result = await AuthService.sendEmailOtp(email, "verify_account");
      const expiresAt = result.expiresAt
        ? Date.parse(result.expiresAt)
        : Date.now() + (result.expiresInSeconds ?? 600) * 1000;
      setOtpExpiresAtMs(expiresAt);
      setResendCooldown(30);
      toast.success(otpLabels.sent || "Verifieringskod skickad");
    } catch (error: any) {
      toast.error(
        getEmailOtpErrorMessage(
          error,
          {
            emailExists: otpLabels.emailExists,
            rateLimited: otpLabels.rateLimited,
            alreadyUsed: otpLabels.alreadyUsed,
            expired: otpLabels.expired,
            invalidCode: otpLabels.invalidCode,
            sendError: otpLabels.sendError,
            generic: labels.error?.somethingWentWrong,
          },
          "send",
        ),
      );
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerify = async (codeOverride?: string) => {
    const code = (codeOverride ?? otpCode).trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error(otpLabels.invalidCode || "Ange 6 siffror");
      return;
    }
    setOtpVerifying(true);
    try {
      const loginResponse = await AuthService.verifyEmailOtpAndLogin(
        email,
        code,
        password,
      );
      toast.success(otpLabels.verified || "E-post verifierad");
      onVerified(loginResponse);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(
        getEmailOtpErrorMessage(
          error,
          {
            rateLimited: otpLabels.rateLimited,
            alreadyUsed: otpLabels.alreadyUsed,
            expired: otpLabels.expired,
            invalidCode: otpLabels.invalidCode,
            verifyError: otpLabels.verifyError,
            generic: labels.error?.somethingWentWrong,
          },
          "verify",
        ),
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {otpLabels.title || "Verifiera e-post"}
          </DialogTitle>
        </DialogHeader>
        <EmailOtpStep
          email={email}
          otpCode={otpCode}
          onOtpCodeChange={setOtpCode}
          onVerify={(code) => void handleVerify(code)}
          onResend={() => void sendOtp()}
          otpSending={otpSending}
          otpVerifying={otpVerifying}
          otpSecondsLeft={otpSecondsLeft}
          otpExpiresAtMs={otpExpiresAtMs}
          resendCooldown={resendCooldown}
          formatMmSs={formatMmSs}
          labels={{
            title: otpLabels.title,
            description: otpLabels.description,
            codeLabel: otpLabels.codeLabel,
            expiresIn: otpLabels.expiresIn,
            expired: otpLabels.expired,
            resend: otpLabels.resend,
            resendIn: otpLabels.resendIn,
            sending: otpLabels.sending,
            continue: otpLabels.continueLogin || otpLabels.continue || "Verifiera och logga in",
            continuing: otpLabels.continuing,
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EmailOtpLoginDialog;
